import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Info, X, XCircle } from 'lucide-react';
import { cn } from '../utils/helpers';

const ToastContext = createContext(null);

const toastStyles = {
  success: {
    icon: CheckCircle,
    className: 'border-green-500/20 bg-green-500/10 text-green-100',
    iconClassName: 'text-green-300'
  },
  error: {
    icon: XCircle,
    className: 'border-red-500/20 bg-red-500/10 text-red-100',
    iconClassName: 'text-red-300'
  },
  warning: {
    icon: AlertCircle,
    className: 'border-yellow-500/20 bg-yellow-500/10 text-yellow-100',
    iconClassName: 'text-yellow-300'
  },
  info: {
    icon: Info,
    className: 'border-blue-500/20 bg-blue-500/10 text-blue-100',
    iconClassName: 'text-blue-300'
  }
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', options = {}) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const toast = {
      id,
      message: String(message || ''),
      type,
      title: options.title,
      duration: options.duration ?? 3800
    };
    setToasts(current => [toast, ...current].slice(0, 4));
    return id;
  }, []);

  useEffect(() => {
    window.BarberFlowToast = showToast;
    return () => {
      if (window.BarberFlowToast === showToast) delete window.BarberFlowToast;
    };
  }, [showToast]);

  useEffect(() => {
    const timers = toasts.map(toast => (
      setTimeout(() => removeToast(toast.id), toast.duration)
    ));
    return () => timers.forEach(clearTimeout);
  }, [removeToast, toasts]);

  const value = useMemo(() => ({
    showToast,
    success: (message, options) => showToast(message, 'success', options),
    error: (message, options) => showToast(message, 'error', options),
    warning: (message, options) => showToast(message, 'warning', options),
    info: (message, options) => showToast(message, 'info', options)
  }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-4 z-[90] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-3 sm:right-6 sm:top-6">
        {toasts.map(toast => {
          const style = toastStyles[toast.type] || toastStyles.info;
          const Icon = style.icon;
          return (
            <div
              key={toast.id}
              className={cn(
                'animate-slide-up overflow-hidden rounded-2xl border p-4 shadow-2xl backdrop-blur-2xl',
                'bg-dark-900/90',
                style.className
              )}
            >
              <div className="flex items-start gap-3">
                <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', style.iconClassName)} />
                <div className="min-w-0 flex-1">
                  {toast.title && <p className="font-semibold text-white">{toast.title}</p>}
                  <p className="text-sm leading-5">{toast.message}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeToast(toast.id)}
                  className="rounded-lg p-1 text-white/50 transition hover:bg-white/10 hover:text-white"
                  aria-label="Fechar aviso"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    return {
      showToast: () => {},
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {}
    };
  }
  return context;
}
